import { OptionallyVisible } from 'components';
import { MouseEvent, useState } from 'react';
import { WithTranslation, useTranslation, withTranslation } from 'react-i18next';
import { BridgeFiatRail, MerchantInfo, PayoutSchedule as PayoutScheduleType, SimulateDepositResponse } from 'services/merchants/types';

import { Button } from 'components/buttons';
import { Panel } from 'components/containers';
import { DataTableRow } from 'components/dataTable';
import DataTable from 'components/dataTable/dataTable';
import IconCalendar from 'components/icons/calendar';
import IconCross from 'components/icons/cross';
import IconRefundAlt from 'components/icons/refundAlt';
import IconUpdate24 from 'components/icons/update24';
import LoadingIndicator from 'components/loadingIndicator';
import { ModalControls } from 'components/modal/styles';

import { CurrencyCode } from 'types/currency';
import { TablePagination } from 'types/pagination';
import { TableSorting } from 'types/sort';

import { formatTableDate } from 'utils/date';
import { formatFiatCurrencyI18n } from 'utils/i18n';

import PayoutSchedule from '../payoutModal/fiat/schedule';
import { ModalContent, StyledDialog } from '../payoutModal/styles';
import { PayoutType } from '../payoutModal/types';
import { PayoutCell } from './components/cells';
import { COLUMNS, LABELS } from './keys';
import {
  DataBar,
  DataTableContainer,
  EditPayoutScheduleButton,
  PayoutsTablePlaceholder,
  Section,
  SectionContent,
  SectionTitle,
} from './styles';
import { PayoutDataRow } from './types';
import { getPayoutThresholdMessage } from './utils';

interface AccountsSectionComponentProps extends WithTranslation {
  payouts: PayoutDataRow[];
  isLoading: boolean;
  pagination: TablePagination;
  onChangePage: (page: number) => void;
  onChangeRowsPerPage: (itemsPerPage: number) => void;
  onSort: (event: MouseEvent, property: string) => void;
  sorting: TableSorting;
  onRowClick: (row: DataTableRow) => void;
  merchantData?: MerchantInfo;
  payout: Payout;
  editScheduleProps: {
    initialValues?: PayoutScheduleType;
    merchant?: MerchantInfo;
    isEditAvailable: boolean;
    payoutType?: PayoutType;
  };
  paymentRail?: BridgeFiatRail;
}

interface Payout {
  simulatePayout: () => Promise<SimulateDepositResponse>;
  isPayoutInProgress: boolean;
}

const PayoutsSectionComponent = ({
  payouts,
  isLoading,
  pagination,
  onChangePage,
  onChangeRowsPerPage,
  onSort,
  sorting,
  onRowClick,
  merchantData,
  payout,
  editScheduleProps,
  paymentRail,
}: AccountsSectionComponentProps) => {
  const { t, i18n } = useTranslation();
  const [isEditingSchedule, setIsEditingSchedule] = useState(false);

  const hasPayouts = Boolean(payouts.length);
  const hasMerchantData = Boolean(merchantData);
  const payoutThresholdMessage = getPayoutThresholdMessage({
    currency: merchantData?.mainFiatCurrency ?? CurrencyCode.USD,
    minAmount: merchantData?.payout.paymentRailConfig?.minAmount ?? 0,
    nextPayoutAmount: merchantData?.payout.nextPayoutAmount,
    paymentRail,
  });

  const onEditScheduleClick = () => {
    setIsEditingSchedule(true);
  };

  const onEditScheduleExit = () => {
    setIsEditingSchedule(false);
  };

  if (isLoading) {
    return (
      <Panel label={t(LABELS.PANEL_TITLE)}>
        <OptionallyVisible visible={isLoading}>
          <LoadingIndicator padded />
        </OptionallyVisible>
      </Panel>
    );
  }

  return (
    <>
      <Panel label={t(LABELS.PANEL_TITLE)}>
        <OptionallyVisible visible={hasMerchantData}>
          <DataBar hasPayouts={hasPayouts}>
            <Section>
              <SectionTitle>
                <IconRefundAlt />
                {t(LABELS.MERCHANT_INFO.BALANCE)}
              </SectionTitle>{' '}
              <SectionContent>
                {formatFiatCurrencyI18n(i18n.language)(
                  Number(merchantData?.payout.nextPayoutAmount),
                  merchantData?.mainFiatCurrency ?? CurrencyCode.USD,
                )}
              </SectionContent>
            </Section>
            <Section>
              <SectionTitle>
                <IconUpdate24 />
                {t(LABELS.MERCHANT_INFO.DATE)}
              </SectionTitle>{' '}
              <SectionContent>{payoutThresholdMessage ?? formatTableDate(merchantData?.payout.nextPayout) ?? '-'}</SectionContent>
            </Section>
            {/* <OptionallyVisible visible={isPayoutAvailable}>
              <StyledButton primary onClick={simulatePayout} disabled={isPayoutInProgress}>
                {t(LABELS.MERCHANT_INFO.SIMULATE_PAYOUT)}
              </StyledButton>
            </OptionallyVisible> */}
            <EditPayoutScheduleButton flat onClick={onEditScheduleClick} disabled={!editScheduleProps.isEditAvailable}>
              <IconCalendar />
              {t(LABELS.EDIT_SCHEDULE.EDIT_ACTION)}
            </EditPayoutScheduleButton>
          </DataBar>
        </OptionallyVisible>
        <DataTableContainer>
          <DataTable
            columns={COLUMNS}
            rows={payouts}
            order={sorting.order}
            orderBy={sorting.orderBy}
            onSort={onSort}
            onRowClick={onRowClick}
            CellComponent={PayoutCell}
            pagination={hasPayouts ? pagination : undefined}
            onChangePage={onChangePage}
            onChangeRowsPerPage={onChangeRowsPerPage}
            placeholder={<PayoutsTablePlaceholder>{t(LABELS.PAYOUTS.NO_PAYOUTS)}</PayoutsTablePlaceholder>}
          />
        </DataTableContainer>
      </Panel>

      <StyledDialog open={isEditingSchedule} scroll="body" disableEscapeKeyDown fullWidth>
        <Panel
          label={t(LABELS.EDIT_SCHEDULE.TITLE)}
          controls={
            <ModalControls>
              <Button flat onClick={onEditScheduleExit}>
                <IconCross />
              </Button>
            </ModalControls>
          }
        >
          <ModalContent>
            <OptionallyVisible visible={Boolean(editScheduleProps.payoutType)}>
              <PayoutSchedule
                initialValues={editScheduleProps.initialValues}
                merchant={editScheduleProps.merchant}
                payoutType={editScheduleProps.payoutType!}
                onSuccess={onEditScheduleExit}
                onClose={onEditScheduleExit}
              />
            </OptionallyVisible>
          </ModalContent>
        </Panel>
      </StyledDialog>
    </>
  );
};

export const PayoutsSection = withTranslation()(PayoutsSectionComponent);
